<template>
  <div :class="props.class">
    <q-table
      v-if="config.render.value"
      ref="table"
      v-model:pagination="dashboard.pagination"
      :rows="props.tableData"
      :row-key="props.rowKey"
      :table-row-style-fn="rowStyleFn"
      rows-per-page-label=""
      :columns="columnsWithDefaults"
      :visible-columns="visibleColumnNames"
      :virtual-scroll-item-size="71"
      :virtual-scroll-sticky-size-start="headerHeight"
      wrap-cells
      :loading="isLoading"
      color="primary"
      class="dashboard-table"
      :class="showHeaderFilters ? 'dashboard-table--with-filters' : ''"
      flat
      :no-data-label="props.noDataLabel"
      :no-results-label="props.noResultsLabel"
      :grid="$q.screen.lt.md"
      :grid-header="$q.screen.lt.md"
      :dense="$q.screen.md"
      :table-style="{ maxHeight }"
      virtual-scroll
      @update:pagination="onPagination"
      @row-click="(evt, row, index) => emit('row-click', evt, row, index)"
      @virtual-scroll="onScroll"
    >
      <!-- #top slot (from table-top) -->
      <template
        v-if="$slots['table-top']"
        #top="scope"
      >
        <slot
          name="table-top"
          v-bind="scope"
        />
      </template>

      <!-- Default #header slot logic -->
      <template #header="scope">
        <slot
          v-if="$slots.header"
          name="header"
          v-bind="scope"
        />
        <DashboardHeader
          v-else
          :slot-props="scope"
          :show-filters="props.showHeaderFilters"
          :model-value="props.colFilters"
          @update:model-value="(newFilters) => emit('update:colFilters', newFilters)"
        />
      </template>

      <!-- Default #item slot logic for grid mode -->
      <template #item="scope">
        <slot
          v-if="$slots.item"
          name="item"
          v-bind="scope"
        />
        <RequestCard
          v-else
          v-bind="scope"
          :on-update="() => emit('refetch')"
        />
      </template>

      <!-- When isExpandable is passed we overwrite the default body slot -->
      <template
        v-if="isExpandable"
        #body="bodyProps"
      >
        <q-tr
          :props="bodyProps"
          class="dashboard-table__row"
          :class="{ 'dashboard-table__row--expanded': isRowExpanded(bodyProps.row) }"
          @click.stop.prevent="toggleRowExpand(bodyProps.row)"
        >
          <template
            v-for="(column, index) in filterMainCols(bodyProps.cols)"
            :key="column.name"
          >
            <q-td :data-label="(column.label || column.name).toLowerCase()">
              <div
                class="row items-center no-wrap q-pa-sm"
                :class="`justify-${column.align || 'left'}`"
              >
                <q-icon
                  v-if="index === 0"
                  size="sm"
                  color="secondary"
                  class="q-mr-sm"
                  :name="isRowExpanded(bodyProps.row) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                />

                <slot
                  v-if="$slots[`body-cell-${column.name}`]"
                  :name="`body-cell-${column.name}`"
                  v-bind="bodyProps"
                />
                <component
                  :is="column.component"
                  v-else-if="column.component"
                  v-bind="{ ...bodyProps, value: column.value, ...column.props }"
                />
                <span v-else>
                  {{ column.value }}
                </span>
              </div>
            </q-td>
          </template>
        </q-tr>
        <!-- q-virtual-scroll--with-prev is necessary when combining expandable rows with virtual scroll -->
        <q-tr
          v-show="isRowExpanded(bodyProps.row)"
          :props="bodyProps"
          class="q-virtual-scroll--with-prev"
        >
          <q-td
            colspan="100%"
            class="dashboard-table__expanded-cell"
          >
            <div class="row re-gutter q-px-xl q-py-md">
              <div
                v-for="column in filterNonMainCols(bodyProps.cols)"
                :key="column.name"
                class="col-md-2 col-6"
              >
                <div class="text-caption">
                  {{ column.label }}
                </div>

                <component
                  :is="column.component"
                  v-if="column.component"
                  v-bind="{ ...bodyProps, value: column.value, ...column.props }"
                />
                <span v-else>
                  {{ column.value || t('monolith.global.na') }}
                </span>
              </div>
            </div>
          </q-td>
        </q-tr>
      </template>

      <!-- By default (without isExpandable), we don't overwrite the body slot and instead render each cell normally -->
      <template
        v-for="column in columnsWithDefaults"
        :key="column.name"
        #[`body-cell-${column.name}`]="scope"
      >
        <q-td
          :props="scope"
          :data-label="(column.label || column.name).toLowerCase()"
        >
          <slot
            v-if="$slots[`body-cell-${column.name}`]"
            :name="`body-cell-${column.name}`"
            v-bind="scope"
          />
          <component
            :is="column.component"
            v-else-if="column.component"
            v-bind="scope"
          />
          <template v-else>
            {{ scope.value }}
          </template>
        </q-td>
      </template>

      <template #bottom>
        <div class="row full-width justify-between items-center">
          <div class="text-center">
            {{ t('dashboard.table.displaying_items', { value: totalRows }) }}
          </div>
          <re-button
            :disabled="isLoading || !hasNextPage || isUsingDateFilters"
            size="xs"
            @click="loadMoreDays()"
          >
            {{ t('dashboard.table.load_more') }}
            <q-spinner
              v-if="isFetchingNextPage"
              class="q-ml-md"
            />
            <q-icon
              v-else
              class="q-ml-md"
              name="mdi-arrow-down"
            />
          </re-button>
        </div>
      </template>
      <template #no-data>
        <div class="row full-width justify-between items-center">
          <div class="text-center">
            {{ t('dashboard.table.no_data') }}
          </div>
          <re-button
            :disabled="isLoading || !hasNextPage || isUsingDateFilters"
            size="xs"
            @click="loadMoreDays()"
          >
            {{ t('dashboard.table.load_more') }}
            <q-spinner
              v-if="isFetchingNextPage"
              class="q-ml-md"
            />
            <q-icon
              v-else
              class="q-ml-md"
              name="mdi-arrow-down"
            />
          </re-button>
        </div>
      </template>
    </q-table>
  </div>
</template>

<script setup lang="ts">
import { until } from '@vueuse/core'
import { useQuasar } from 'quasar'
import { computed, inject, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useBaseDashboard } from '@/apps/MainApp/views/Dashboard/Base/core/builder'
import { DashboardColumn } from '@/apps/MainApp/views/Dashboard/Base/core/builder'
import DashboardHeader from '@/apps/MainApp/views/Dashboard/Base/core/slots/DashboardHeader.vue'
import RequestCard from '@/apps/MainApp/views/Dashboard/Base/core/slots/RequestCard.vue'
import { requestListKey } from '@/keys'

const props = defineProps({
  // Table configuration
  tableData: { type: Array, default: () => [] },
  loading: { type: [Boolean, Object], default: null },
  rowKey: { type: String, default: 'id' },
  class: { type: [String, Object, Array], default: () => ({}) },
  noDataLabel: { type: String, default: 'No data' },
  noResultsLabel: { type: String, default: 'No results' },
  isExpandable: { type: Boolean, default: false },

  // Filtering
  showHeaderFilters: { type: Boolean, default: false },
  colFilters: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['update:pagination', 'update:colFilters', 'row-click', 'refetch'])

const expanded = defineModel('expanded', { type: Array, default: () => [] })
const expandedSet = computed(() => new Set(expanded.value))

const MAIN_COLUMNS = ['customer', 'displayAddress', 'customer_ref', 'status', 'valuation_type', 'action']

const dashboard = useBaseDashboard(false)
const { t, te } = useI18n()
const $q = useQuasar()
const { hasNextPage, loadMoreDays, isUsingDateFilters, isFetchingNextPage, setPagination } =
  inject(requestListKey)!
const config = dashboard.getConfig()

const table = ref()
const TABLE_HEIGHT = '800px'
const maxHeight = computed(() => `calc(${TABLE_HEIGHT} - ${headerHeight.value}px)`)

const columnsWithDefaults = computed<DashboardColumn[]>(() => {
  return config.columns.value.map((column: DashboardColumn) => ({
    sortable: column.sortable ?? false,
    visible: column.visible !== false,
    align: column.align || 'left',
    ...column,
    field: column.field || column.name, // Override after spread to ensure correct field
    label: te(column.label) ? t(column.label) : column.label || '',
    isMain: props.isExpandable ? MAIN_COLUMNS.includes(column.name) : true,
  }))
})

const isLoading = computed<boolean>(() =>
  props.loading === null ? dashboard.isLoading.value : !!props.loading
)
const headerHeight = computed(() => {
  return table.value?.$el?.getElementsByTagName('thead')?.[0]?.getBoundingClientRect().height
})

// Names of visible columns
const visibleColumnNames = computed(() => {
  return columnsWithDefaults.value
    .filter((col: DashboardColumn) => col.visible)
    .map((col: DashboardColumn) => col.name)
})

// Helper functions for filtering cols in template (avoids implicit any)

const filterMainCols = (cols: DashboardColumn[]) => cols.filter((col) => col.isMain)

const filterNonMainCols = (cols: DashboardColumn[]) => cols.filter((col) => !col.isMain && col.value != null)

const rowStyleFn = (row: Record<string, unknown>): string => {
  const index = props.tableData.indexOf(row)
  return index % 2 === 0 ? 'background-color: var(--base)' : 'background-color: var(--light)'
}

interface TablePagination {
  sortBy?: string | null
  descending?: boolean
  page?: number
  rowsPerPage?: number
  rowsNumber?: number
}

const onPagination = (value: TablePagination) => {
  if (setPagination) {
    setPagination(value as Parameters<typeof setPagination>[0])
    nextTick(() => {
      table.value?.scrollTo(0, 'force-start')
    })
  }
}

const totalRows = computed(() => table.value?.computedRowsNumber)

interface VirtualScrollEvent {
  index: number
  from: number
  to: number
  direction: 'increase' | 'decrease'
}

async function onScroll({ to, direction }: VirtualScrollEvent) {
  await until(isLoading).toBe(false)

  // do not attempt to load more data if we're using date filters or no more pages
  if (
    direction === 'increase' &&
    totalRows.value - 1 === to &&
    !isUsingDateFilters.value &&
    hasNextPage.value
  ) {
    loadMoreDays()
    return
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toggleRowExpand = (row: any) => {
  const rowId = row[props.rowKey]
  const currentSet = new Set(expanded.value)

  if (currentSet.has(rowId)) {
    currentSet.delete(rowId)
  } else {
    currentSet.add(rowId)
  }

  expanded.value = Array.from(currentSet)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isRowExpanded = (row: any) => {
  return expandedSet.value.has(row[props.rowKey])
}

// Auto-fetch next page when table is empty but more data exists
// Because the onScroll function is not triggered when there are no results initially
watch(
  [() => props.tableData.length, hasNextPage, isLoading, isUsingDateFilters],
  ([length, hasNext, loading, usingDateFilters]) => {
    if (length === 0 && hasNext && !loading && !usingDateFilters) {
      loadMoreDays()
    }
  },
  { immediate: true }
)
</script>

<style lang="scss">
:root {
  --header-height: 28px;
}
</style>

<style lang="scss" scoped>
.dashboard-table {
  &--with-filters {
    --header-height: 91px;
  }

  :deep(thead) {
    position: sticky;
    z-index: 1;
    top: 0;
    white-space: nowrap;

    // documentation recommends this but it seems to have no effect besides causing issues with loading state not showing
    // tr:last-child th {
    //     /* height of all previous header rows */
    //     bottom: var(--header-height);
    // }

    // thead tr:first-child th {
    //     top: 0;
    // }
  }

  /* prevent scrolling behind sticky top row on focus */
  :deep(tbody) {
    scroll-margin-top: var(--header-height);
  }

  &__row--expanded,
  &__row--expanded + .q-tr {
    background-color: var(--light);
  }

  &__row--expanded {
    // hack to show hover bg from quasar on combined rows
    &:hover {
      + .q-tr {
        td:before {
          content: '';
        }
      }
    }

    td {
      border: none !important;
    }

    // hack to show hover bg from quasar on combined rows
    &:has(+ .q-tr:hover) {
      td:before {
        content: '';
      }
    }
  }

  &__row {
    .action-buttons {
      max-width: 270px;
    }
  }

  &__expanded-cell {
    padding-left: 5rem !important;
  }
}
</style>
